import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { buildBlockReviews, computeKpis, kpiDeltas,
         type RawPest, type RawBlock, type BlockReview, type ReviewKpis } from '../../../scouting/reviewModel';

interface FieldReviewResponse {
  pests: RawPest[];
  current: { blocks: RawBlock[] };
  previous: { blocks: RawBlock[] };
}

export interface UseFieldReview {
  loading: boolean;
  error: string | null;
  pests: RawPest[];
  current: BlockReview[];
  previous: BlockReview[];
  kpis: ReviewKpis;
  deltas: ReturnType<typeof kpiDeltas>;
  hasPrevious: boolean;
  refetch: () => void;
}

const EMPTY_KPIS: ReviewKpis = { blocksOverThreshold: 0, totalBlocks: 0, pestBlockExceedances: 0, approaching: 0, avgCoveragePct: 0 };

export function useFieldReview(): UseFieldReview {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pests, setPests] = useState<RawPest[]>([]);
  const [current, setCurrent] = useState<BlockReview[]>([]);
  const [previous, setPrevious] = useState<BlockReview[]>([]);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [nonce, setNonce] = useState(0);

  const refetch = useCallback(() => setNonce(n => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get<FieldReviewResponse>('/api/v1/scouting/field-review', { headers })
      .then(res => {
        if (cancelled) return;
        const data = res.data;
        setPests(data.pests || []);
        setCurrent(buildBlockReviews(data.current?.blocks || [], data.pests || []));
        setPrevious(buildBlockReviews(data.previous?.blocks || [], data.pests || []));
        setHasPrevious((data.previous?.blocks || []).length > 0);
      })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load review'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [nonce]);

  const kpis = computeKpis(current);
  const deltas = kpiDeltas(kpis, hasPrevious ? computeKpis(previous) : EMPTY_KPIS);
  return { loading, error, pests, current, previous, kpis, deltas, hasPrevious, refetch };
}
